<script setup lang="ts">
/**
 * RinganFaq — bagian FAQ untuk halaman kompres. Padanan <CaFaqBagian>.
 *
 * KENAPA <details>, BUKAN AKORDEON:
 * <CaFaqAccordion> membungkus <UAccordion> milik Nuxt UI, yang di balik layar
 * adalah reka-ui — komponen paling mahal di seluruh halaman ini, dipakai hanya
 * untuk membuka-tutup lima paragraf teks. <details>/<summary> melakukan hal
 * yang sama secara bawaan peramban: nol JavaScript, tetap bisa dibuka meski JS
 * gagal dimuat, dan sudah punya semantik aksesibilitas yang benar.
 *
 * Yang PALING penting untuk halaman ini: isi <details> SELALU ada di HTML yang
 * dikirim server, tertutup maupun terbuka. Itu syarat mati di sini. Bawaan
 * reka-ui adalah `unmountOnHide: true` — panel tertutup tidak ada di DOM sama
 * sekali, sehingga seluruh jawaban FAQ hilang dari HTML dan baru lahir setelah
 * JS jalan. Ini pernah benar-benar terjadi di situs ini (jawaban FAQ sempat
 * hanya tersisa di dalam blok JSON-LD), dan CaFaqAccordion sekarang menyalakan
 * `selaluRender` untuk menambalnya. Dengan <details>, masalah itu tidak punya
 * tempat untuk kambuh: tidak ada sakelar yang bisa salah disetel.
 *
 * TAMPILANNYA SENGAJA IDENTIK dengan akordeon di layout utama — kelas pembungkus,
 * pemicu, dan panelnya disalin dari objek `ui` di CaFaqAccordion.vue, termasuk
 * panah yang berputar 180°. Pengunjung tidak boleh merasa mendarat di situs lain.
 *
 * Gerak mengikuti aturan v3: tinggi berubah SEKETIKA (nol animasi layout, yang
 * menyendat di HP Android murah), dan hanya opacity + geser 4px pada isinya
 * plus rotasi panah yang dihaluskan — keduanya di-composite GPU.
 */
import type { Faq } from '~/composables/useSeo'

withDefaults(
  defineProps<{
    daftar: Faq[]
    judul?: string
    badge?: string
    badgeIkon?: string
  }>(),
  {
    judul: 'Pertanyaan yang sering ditanyakan',
    badge: '',
    badgeIkon: 'message-circle-question'
  }
)
</script>

<template>
  <div>
    <RinganJudulBagian :badge="badge || undefined" :badge-ikon="badgeIkon" :judul="judul" />

    <div class="mt-7 max-w-3xl">
      <div class="w-full divide-y divide-line border border-line rounded-card overflow-hidden bg-surface">
        <details v-for="(f, i) in daftar" :key="i" class="ringan-faq group">
          <summary
            class="ca-focus flex w-full items-center gap-3 px-5 py-4 min-h-14 text-left text-[0.9375rem] font-semibold text-ink hover:bg-surface-2 transition-colors cursor-pointer"
          >
            <span class="text-start break-words">{{ f.tanya }}</span>
            <RinganIkon
              nama="chevron-down"
              class="ringan-faq-panah size-5 text-muted shrink-0 ms-auto"
            />
          </summary>
          <div class="ringan-faq-isi px-5 pb-4 text-sm leading-relaxed text-muted">{{ f.jawab }}</div>
        </details>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Segitiga bawaan peramban dimatikan; penandanya adalah panah chevron kita
   sendiri, sama seperti akordeon di layout utama. */
.ringan-faq > summary {
  list-style: none;
}
.ringan-faq > summary::-webkit-details-marker {
  display: none;
}

.ringan-faq-panah {
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1);
}
.ringan-faq[open] .ringan-faq-panah {
  transform: rotate(180deg);
}

/* Isi memudar masuk tanpa satu pun frame yang menganimasikan layout.
   @starting-style memberi keadaan awal untuk elemen yang baru mulai dirender;
   di WebView Android lama yang belum mengenalnya, aturan ini diabaikan dan isi
   panel muncul seketika — tidak ada yang rusak. */
.ringan-faq-isi {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  transition:
    opacity 180ms cubic-bezier(0.2, 0, 0, 1),
    transform 180ms cubic-bezier(0.2, 0, 0, 1);
}

@starting-style {
  .ringan-faq[open] .ringan-faq-isi {
    opacity: 0;
    transform: translate3d(0, -4px, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ringan-faq-panah,
  .ringan-faq-isi {
    transition: none !important;
  }
  .ringan-faq-isi {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>
